
import { supabase } from '@/integrations/supabase/client';
import { StorageApiClient } from '../types/storage-api-types';

// Configuration for storage providers
export interface StorageConfig {
  provider: 'supabase' | 's3' | 'custom';
  bucketName: string;
  s3Config?: {
    region: string;
    endpoint?: string;
    accessKeyId?: string;
    secretAccessKey?: string;
  };
  customConfig?: {
    uploadEndpoint: string;
    deleteEndpoint: string;
    fetchEndpoint: string;
  };
}

// Read bucket name from environment variable or use default
const BUCKET_NAME = import.meta.env.VITE_STORAGE_BUCKET_NAME || 'gotrekking';

// Default configuration using Supabase
const defaultConfig: StorageConfig = {
  provider: 'supabase',
  bucketName: BUCKET_NAME
};

export class SupabaseStorageApiClient implements StorageApiClient {
  private config: StorageConfig = defaultConfig;
  
  setStorageConfig(config: StorageConfig): StorageConfig {
    this.config = config;
    return this.config;
  }
  
  getStorageConfig(): StorageConfig {
    return this.config;
  }
  
  async uploadFile(file: File, path: string): Promise<string> {
    console.log(`Uploading file to Supabase storage, bucket: ${this.config.bucketName}, path: ${path}`);
    
    try {
      const { data, error } = await supabase.storage
        .from(this.config.bucketName)
        .upload(path, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) {
        console.error('Upload error:', error);
        throw error;
      }

      console.log('Upload successful, data:', data);
      return this.getFileUrl(path);
    } catch (error) {
      console.error('Error in uploadFile:', error);
      throw error;
    }
  }
  
  async deleteFile(path: string): Promise<void> {
    console.log(`Deleting file from Supabase storage, bucket: ${this.config.bucketName}, path: ${path}`);
    
    try {
      const { error } = await supabase.storage
        .from(this.config.bucketName)
        .remove([path]);

      if (error) {
        console.error('Delete error:', error);
        throw error;
      }
      
      console.log('Delete successful');
    } catch (error) {
      console.error('Error in deleteFile:', error);
      throw error;
    }
  }
  
  async getFileUrl(path: string, fallbackUrl?: string): Promise<string> {
    console.log(`Getting file URL from Supabase storage, bucket: ${this.config.bucketName}, path: ${path}`);
    
    try {
      const { data } = await supabase.storage
        .from(this.config.bucketName)
        .getPublicUrl(path);
        
      if (!data) {
        throw new Error('No data returned from storage');
      }
      
      // Check if the URL actually points to an existing image
      try {
        const response = await fetch(data.publicUrl, { method: 'HEAD' });
        if (!response.ok) {
          throw new Error(`Image not found: ${response.status}`);
        }
      } catch (err) {
        // If fetch fails, but we have a fallback, return it
        if (fallbackUrl) return fallbackUrl;
        throw err;
      }
      
      console.log('Got public URL:', data.publicUrl);
      return data.publicUrl;
    } catch (error) {
      console.error('Error in getFileUrl:', error);
      if (fallbackUrl) return fallbackUrl;
      return 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?q=80&w=500&auto=format&fit=crop';
    }
  }
}

export const supabaseStorageClient = new SupabaseStorageApiClient();
